interface Props {
  seed: string;
  cellSize?: number;
  color?: string; // e.g. "255,255,255"
  opacity?: number;
  link?: string; // 클릭 시 이동할 URL (스캔 시뮬레이션)
  tooltip?: string; // hover 시 표시할 텍스트
}

/**
 * QR-코드 스타일 장식 요소 – 실제 QR 규격 모방
 * (3개의 파인더 패턴 코너 + 시드 기반 랜덤 데이터 영역)
 * link prop 제공 시 클릭 → 해당 URL 오픈 (QR 스캔 시뮬레이션)
 */
export default function QRCodeDecor({ seed, cellSize = 3, color = "255,255,255", opacity = 0.3, link, tooltip }: Props) {
  const N = 11;

  // 간단한 djb2 해시
  let h = 5381;
  for (let i = 0; i < seed.length; i++) {
    h = ((h << 5) - h + seed.charCodeAt(i)) | 0;
  }
  h = Math.abs(h) + 1;

  const cells = Array.from({ length: N * N }, (_, idx) => {
    const r = Math.floor(idx / N);
    const c = idx % N;

    // 파인더 패턴 (세 귀퉁이 3×3)
    const tl = r <= 2 && c <= 2;
    const tr = r <= 2 && c >= N - 3;
    const bl = r >= N - 3 && c <= 2;

    if (tl || tr || bl) {
      const lr = r <= 2 ? r : r - (N - 3);
      const lc = c <= 2 ? c : c - (N - 3);
      // 외곽 테두리 + 중앙 점
      return lr === 0 || lr === 2 || lc === 0 || lc === 2 || (lr === 1 && lc === 1);
    }

    // 파인더 주변 여백
    if ((r <= 3 && c <= 3) || (r <= 3 && c >= N - 4) || (r >= N - 4 && c <= 3)) return false;

    // 타이밍 패턴
    if (r === 4 && c % 2 === 0) return true;
    if (c === 4 && r % 2 === 0) return true;

    // 데이터 영역 (시드 기반)
    return Boolean(((h ^ (h >>> (idx % 7)) ^ (idx * 1664525 + 1013904223)) & 1));
  });

  const totalSize = N * cellSize + (N - 1);

  const grid = (
    <div
      title={tooltip ?? (link ? "클릭하여 프로필 열기" : undefined)}
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${N}, ${cellSize}px)`,
        gridTemplateRows: `repeat(${N}, ${cellSize}px)`,
        gap: "1px",
        width: totalSize,
        height: totalSize,
        flexShrink: 0,
        cursor: link ? "pointer" : "default",
        transition: "opacity 0.15s",
      }}
    >
      {cells.map((filled, i) => (
        <div
          key={i}
          style={{
            width: cellSize,
            height: cellSize,
            backgroundColor: filled ? `rgba(${color},${opacity})` : "transparent",
            borderRadius: "0.5px",
          }}
        />
      ))}
    </div>
  );

  if (link) {
    return (
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        title={tooltip ?? "클릭하여 프로필 열기"}
        onClick={(e) => e.stopPropagation()}
        style={{ display: "inline-flex", flexShrink: 0 }}
      >
        {grid}
      </a>
    );
  }

  return grid;
}
